/**
 * Saví Sava — PDP Redesign interactions.
 *
 * Handles, per `product-form-pdp` element on the page:
 *  - color-circle / size-button selection -> matching variant lookup
 *  - updating price, save badge, stock note, add-to-bag label + disabled state
 *  - unified swipeable gallery (slideshow-component) shared by desktop and
 *    mobile, with synced counter / thumbnails / dots, and color-click
 *    filtering
 *  - size guide dialog open/close
 *  - real add-to-cart via the Ajax Cart API, with a safe fallback to a normal
 *    form submit if anything goes wrong (so the button never becomes a dead end)
 *  - "Buy It Now" as a genuine native submit straight to checkout
 *
 * No dependency on any Horizon internal JS API — everything here reads plain
 * DOM/data attributes rendered by sections/pdp-redesign.liquid.
 */
(function () {
  function moneyFromCents(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format) return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    return '$' + amount;
  }

  function initProductForm(root, galleryApi) {
    var variantsScript = root.querySelector('[data-pdp-variants]');
    var variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch (e) {
      variants = [];
    }

    var colorIndex = parseInt(root.getAttribute('data-color-option-index'), 10);
    var sizeIndex = parseInt(root.getAttribute('data-size-option-index'), 10);
    var lowStockThreshold = parseInt(root.getAttribute('data-low-stock-threshold'), 10) || 10;

    var section = root.closest('[data-testid^="pdp-redesign-"]') || document;

    var colorRow = root.querySelector('[data-pdp-color-row]');
    var sizeRow = root.querySelector('[data-pdp-size-row]');
    var variantInput = root.querySelector('[data-pdp-variant-input]');
    var addButton = root.querySelector('[data-pdp-add-to-cart]');
    var buyNowButton = root.querySelector('[data-pdp-buy-now]');
    var addLabel = root.querySelector('[data-pdp-add-label]');
    var addPrice = root.querySelector('[data-pdp-add-price]');
    var priceCurrent = section.querySelector('[data-pdp-price]');
    var saveBadge = section.querySelector('[data-pdp-save-badge]');
    var stickyPrice = section.querySelector('[data-pdp-sticky-price]');
    var colorNameLabel = root.querySelector('[data-pdp-color-name]');
    var stockBox = section.querySelector('[data-pdp-stock]');

    function currentSelection() {
      var selection = {};
      if (colorRow) {
        var activeColor = colorRow.querySelector('.pdp__color-circle.is-active');
        if (activeColor) selection.color = activeColor.getAttribute('data-pdp-color-value');
      }
      if (sizeRow) {
        var activeSize = sizeRow.querySelector('.pdp__size-button.is-active');
        if (activeSize) selection.size = activeSize.getAttribute('data-pdp-size-value');
      }
      return selection;
    }

    function findVariant(selection) {
      return variants.filter(function (variant) {
        var options = variant.options || [];
        if (colorIndex > -1 && selection.color != null && options[colorIndex] !== selection.color) return false;
        if (sizeIndex > -1 && selection.size != null && options[sizeIndex] !== selection.size) return false;
        return true;
      })[0];
    }

    function variantExistsAndAvailable(colorValue, sizeValue) {
      var match = variants.filter(function (variant) {
        var options = variant.options || [];
        if (colorIndex > -1 && colorValue != null && options[colorIndex] !== colorValue) return false;
        if (sizeIndex > -1 && sizeValue != null && options[sizeIndex] !== sizeValue) return false;
        return true;
      })[0];
      return { exists: !!match, available: !!(match && match.available) };
    }

    function refreshSizeAvailability() {
      if (!sizeRow) return;
      var selection = currentSelection();
      sizeRow.querySelectorAll('[data-pdp-size-value]').forEach(function (button) {
        var sizeValue = button.getAttribute('data-pdp-size-value');
        var status = variantExistsAndAvailable(selection.color, sizeValue);
        if (!status.exists || !status.available) {
          button.classList.add('is-unavailable');
        } else {
          button.classList.remove('is-unavailable');
        }
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;

      var moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{ amount }}';
      var priceText = moneyFromCents(variant.price, moneyFormat);

      if (priceCurrent) priceCurrent.textContent = priceText;
      if (addPrice) addPrice.textContent = priceText;
      if (stickyPrice) stickyPrice.textContent = priceText;

      if (saveBadge) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saveBadge.textContent = 'SAVE ' + pct + '%';
          saveBadge.style.display = '';
        } else {
          saveBadge.style.display = 'none';
        }
      }

      if (addButton) {
        addButton.disabled = !variant.available;
        if (addLabel) {
          addLabel.innerHTML = variant.available
            ? 'ADD TO BAG — <span data-pdp-add-price>' + priceText + '</span>'
            : 'SOLD OUT';
        }
      }

      if (buyNowButton) buyNowButton.disabled = !variant.available;

      if (stockBox) {
        if (variant.inventory_management === 'shopify') {
          if (variant.inventory_quantity > 0 && variant.inventory_quantity <= lowStockThreshold) {
            stockBox.innerHTML =
              '<span class="pdp__stock-dot pdp__stock-dot--low"></span><span>Low stock — only ' +
              variant.inventory_quantity +
              ' left in this color</span>';
            stockBox.style.display = '';
          } else if (variant.inventory_quantity > lowStockThreshold) {
            stockBox.innerHTML = '<span class="pdp__stock-dot pdp__stock-dot--in"></span><span>In stock</span>';
            stockBox.style.display = '';
          } else {
            stockBox.style.display = 'none';
          }
        }
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    function selectColor(button) {
      if (!colorRow) return;
      colorRow.querySelectorAll('.pdp__color-circle').forEach(function (el) {
        el.classList.remove('is-active');
        el.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      if (colorNameLabel) colorNameLabel.textContent = (button.getAttribute('data-pdp-color-value') || '').toUpperCase();

      refreshSizeAvailability();
      var variant = findVariant(currentSelection());
      if (variant) updateForVariant(variant);

      if (galleryApi && galleryApi.filterByColor) {
        galleryApi.filterByColor(button.getAttribute('data-pdp-color-handle'));
      }
    }

    function selectSize(button) {
      if (!sizeRow || button.classList.contains('is-unavailable')) return;
      sizeRow.querySelectorAll('.pdp__size-button').forEach(function (el) {
        el.classList.remove('is-active');
      });
      button.classList.add('is-active');

      var variant = findVariant(currentSelection());
      if (variant) updateForVariant(variant);
    }

    if (colorRow) {
      colorRow.querySelectorAll('.pdp__color-circle').forEach(function (button) {
        button.addEventListener('click', function () {
          selectColor(button);
        });
      });
      // Apply the initial filter to match whichever color is selected on
      // load (e.g. from a ?variant= URL), so the gallery starts in sync.
      var initialColor = colorRow.querySelector('.pdp__color-circle.is-active');
      if (initialColor && galleryApi && galleryApi.filterByColor) {
        galleryApi.filterByColor(initialColor.getAttribute('data-pdp-color-handle'));
      }
    }

    if (sizeRow) {
      sizeRow.querySelectorAll('.pdp__size-button').forEach(function (button) {
        button.addEventListener('click', function () {
          selectSize(button);
        });
      });
      refreshSizeAvailability();
    }

    // Add to cart: Ajax Cart API with a safe fallback to a real form submit.
    var form = root.querySelector('form[data-type="add-to-cart-form"]');
    if (form && addButton) {
      form.addEventListener('submit', function (event) {
        // "Buy It Now" is a native submit button named "checkout" — let the
        // browser POST the form for real so Shopify's own add-then-redirect-
        // to-/checkout behavior fires. Only intercept the regular Add to Bag
        // submit with the Ajax Cart API.
        if (event.submitter && event.submitter.name === 'checkout') {
          return;
        }

        event.preventDefault();

        var formData = new FormData(form);
        addButton.disabled = true;
        var originalHTML = addButton.innerHTML;
        addButton.innerHTML = '<span>ADDING…</span>';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add to cart failed');
            return response.json();
          })
          .then(function () {
            addButton.innerHTML = '<span>ADDED ✓</span>';

            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            fetch('/cart.js')
              .then(function (r) {
                return r.json();
              })
              .then(function (cart) {
                document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
              })
              .catch(function () {});

            setTimeout(function () {
              addButton.innerHTML = originalHTML;
              addButton.disabled = !addButton.hasAttribute('data-force-disabled');
            }, 1800);
          })
          .catch(function () {
            // Fallback: submit the form for real so the customer's add still goes through.
            addButton.innerHTML = originalHTML;
            addButton.disabled = false;
            HTMLFormElement.prototype.submit.call(form);
          });
      });
    }
  }

  // One unified swipeable gallery shared by desktop and mobile — built on
  // the theme's own slideshow-component (same pattern the homepage
  // carousels use). Desktop's vertical thumb rail and mobile's horizontal
  // thumb strip are just two markup instances of the same
  // `[data-pdp-thumb]` control, both kept in sync with whichever slide is
  // active. Returns an API object ({ filterByColor }) so initProductForm
  // can filter the gallery when a color swatch is clicked.
  function initGallery(root) {
    var gallery = root.querySelector('[data-pdp-gallery]');
    if (!gallery) return null;

    var thumbs = gallery.querySelectorAll('[data-pdp-thumb]');
    var dots = gallery.querySelectorAll('[data-pdp-dots] .pdp__dot');
    var counter = gallery.querySelector('[data-pdp-counter]');
    var scroller = gallery.querySelector('slideshow-slides');
    var slides = gallery.querySelectorAll('.pdp-gallery__slide');
    var total = slides.length;

    function visibleTotal() {
      var count = 0;
      slides.forEach(function (slide) {
        if (!slide.hidden) count++;
      });
      return count || total;
    }

    function setActive(index) {
      thumbs.forEach(function (thumb) {
        thumb.classList.toggle('is-active', parseInt(thumb.getAttribute('data-index'), 10) === index);
      });
      dots.forEach(function (dot, dotIndex) {
        dot.classList.toggle('is-active', dotIndex === index);
      });
      if (counter && total) counter.textContent = index + 1 + ' / ' + visibleTotal();
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        var target = slides[index];
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        }
        setActive(index);
      });
    });

    if (scroller && slides.length) {
      try {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                var index = Array.prototype.indexOf.call(slides, entry.target);
                if (index > -1) setActive(index);
              }
            });
          },
          { root: scroller, threshold: [0.6] }
        );
        slides.forEach(function (slide) {
          observer.observe(slide);
        });
      } catch (e) {
        /* IntersectionObserver unsupported — thumbnails/dots stay clickable regardless */
      }
    }

    // Jump to the selected color while keeping the full gallery available.
    function filterGalleryByColor(colorHandle) {
      if (!colorHandle) return;
      var index = Array.from(slides).findIndex(function (slide) {
        return (slide.getAttribute('data-colors') || '').split(',').indexOf(colorHandle) > -1;
      });
      if (index < 0) return;
      slides[index].scrollIntoView({ behavior: 'auto', inline: 'start', block: 'nearest' });
      setActive(index);
    }

    return { filterByColor: filterGalleryByColor };
  }

  function initSizeGuide(root) {
    var openButton = root.querySelector('[data-pdp-size-guide-open]');
    var dialog = root.querySelector('.pdp__size-guide-dialog');
    if (!openButton || !dialog) return;

    openButton.addEventListener('click', function () {
      try {
        dialog.showModal();
      } catch (e) {
        dialog.setAttribute('open', '');
      }
    });

    var closeButton = dialog.querySelector('[data-pdp-size-guide-close]');
    if (closeButton) {
      closeButton.addEventListener('click', function () {
        try {
          dialog.close();
        } catch (e) {
          dialog.removeAttribute('open');
        }
      });
    }

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        try {
          dialog.close();
        } catch (e) {
          dialog.removeAttribute('open');
        }
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-testid^="pdp-redesign-"]').forEach(function (section) {
      var galleryApi = initGallery(section);
      var form = section.querySelector('product-form-pdp');
      if (form) initProductForm(form, galleryApi);
      initSizeGuide(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
